import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { Search, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const formatCurrency = (v) => v != null ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v) : "$0.00";
const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "\u2014";

export default function Dealers() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");

  const fetchDealers = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 100 };
      if (search) params.search = search;
      if (filter === "overdue") params.overdue_only = true;
      if (filter === "missing") params.missing_payment = true;
      if (filter === "active") params.status = "active";
      const res = await axios.get(`${API}/dealers`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setDealers(res.data.dealers || []);
    } catch (err) {
      toast.error("Failed to load dealers");
    } finally {
      setLoading(false);
    }
  }, [token, search, filter]);

  useEffect(() => { fetchDealers(); }, [fetchDealers]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDealers();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight" data-testid="dealers-title">Dealers</h1>
        <p className="text-sm text-muted-foreground mt-1">Customer list and subscription tracking</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
            <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-sm">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search dealers..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="dealers-search"
                />
              </div>
              <Button type="submit" size="sm" data-testid="dealers-search-btn">Search</Button>
            </form>
            <div className="flex gap-2">
              {["all", "active", "overdue", "missing"].map((f) => (
                <Button
                  key={f}
                  variant={filter === (f === "all" ? "" : f) ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilter(f === "all" ? "" : f)}
                  data-testid={`dealers-filter-${f}`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dealer Name</TableHead>
                <TableHead>First Payment</TableHead>
                <TableHead>Latest Payment</TableHead>
                <TableHead className="text-right">Months Active</TableHead>
                <TableHead className="text-right">Total Gross</TableHead>
                <TableHead className="text-right">Total Invoiced</TableHead>
                <TableHead>Next Expected</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [1,2,3,4,5].map((key) => (
                  <TableRow key={`skeleton-${key}`}><TableCell colSpan={9}><div className="h-8 bg-muted rounded animate-pulse" /></TableCell></TableRow>
                ))
              ) : dealers.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No dealers found</TableCell></TableRow>
              ) : (
                dealers.map((d) => (
                  <TableRow key={d.id} className="table-row-hover cursor-pointer" onClick={() => navigate(`/dealers/${d.id}`)} data-testid={`dealer-row-${d.id}`}>
                    <TableCell className="font-medium">{d.dealer_name}</TableCell>
                    <TableCell className="text-sm">{formatDate(d.first_payment_date)}</TableCell>
                    <TableCell className="text-sm">{formatDate(d.latest_payment_date)}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.months_active}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(d.total_gross_received)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(d.total_invoiced)}</TableCell>
                    <TableCell className="text-sm">{formatDate(d.expected_next_payment_date)}</TableCell>
                    <TableCell><StatusBadge status={d.status} /></TableCell>
                    <TableCell><ChevronRight className="w-4 h-4 text-muted-foreground" /></TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
